import React, {useEffect, useState} from "react";
import {useSelector, useDispatch} from 'react-redux';
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import tagTextures, { defaultTagTexture, getTagTexturePath } from "../viewer/textures/tag-icons";
import Pagination from "@material-ui/lab/Pagination/Pagination";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import {
  addAsset,
  addNewTag,
  AssetType,
  changeTagContent,
  changeTexture,
  deleteAssetData,
  getTagAssets,
  selectAllAssets,
  TagData
} from "../redux/assets-slice";
import {selectAllFolders} from '../redux/folders-slice';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  InputLabel,
  TextField,
  Checkbox,
  Avatar,
  Typography,
  MenuItem
} from "@material-ui/core";
import clsx from 'clsx';
import {
  DraggableDialog,
  PromptDialog,
  EnhancedTable
} from "../components";
import { toast } from "../app";
import { getNewDefaultFolder } from "../utilities";
import LocalScene from "../viewer/js/projections";
import { Viewer } from "../viewer/js/main";
import { useTranslation } from "react-i18next";
import { isStaticSite } from "../electron-modules";

const copyTextToClipboard = require('copy-to-clipboard');

export const tagIconsPerRow = 8;
export const tagRowsPerPage = 4;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    textField: {
      marginBottom: theme.spacing(3)
    },
    selectDivider: {
      margin: theme.spacing(0.5),
      opacity: 0.2
    },
    checkBoxWithLabel: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginRight: "-9px"
    },
    asterisk: {
      "&:after": {
        content:'"*"',
        color: "red",
        paddingLeft: theme.spacing(0.5)
      }
    },
    avatarParent: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    tagAvatar: {
      width: "24px",
      height: "24px",
      cursor: "pointer",
      "&:hover": {
        width: "26px",
        height: "26px",
        margin: "-1px"
      }
    },
    tagIconPagination: {
      paddingBottom: theme.spacing(2)
    },
    tagIconSearchParent: {
      textAlign: "right",
      paddingRight: theme.spacing(2),
      paddingBottom: theme.spacing(1)
    },
    tagIconPageParent: {
      width: "100%",
      display: "flex",
      flexDirection: "row-reverse"
    },
    tagIconParent: {
      position: "relative",
      display: "inline-block",
      width: `calc(${100/tagIconsPerRow}% - ${theme.spacing(2)}px)`,
      padding: theme.spacing(1)
    },
    tagIconCheck: {
      userSelect: "none",
      bottom: theme.spacing(1),
      right: theme.spacing(1),
      position: "absolute",
      color: "green",
      backgroundColor: "white",
      borderRadius: "50%"
    },
    tagIconImage: {
      width: `calc(100% - ${theme.spacing(1)}px)`,
      height: `calc(100% - ${theme.spacing(1)}px)`,
      margin: theme.spacing(0.5),
      cursor: "pointer",
      "&:hover": {
        width: "100%",
        height: "100%",
        margin: "0px",
      }
    },
    tagIconFilename: {
      display: "block",
      margin: theme.spacing(2)
    },
    content: {
      padding: theme.spacing(0),
      paddingTop: "0px !important"
    },
  }),
);

export const TagIconDialog = (props) => {
  const {open, onSubmit, onClose, currentTexture, fileName} = props;

  const classes = useStyles();
  const {t} = useTranslation();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState("");

  const getSearchedTextures = () => {
    const searchText = search.toLowerCase().trim();
    if (searchText === "") {
      return Object.keys(tagTextures);
    }

    const searchTerms = searchText.split(" ");

    return Object.keys(tagTextures)
      .map(key => {
        const itemText = key.toLowerCase();
        const matches = searchTerms.filter(x => itemText.includes(x)).length;
        return {key, matches};
      })
      .filter(x => x.matches >= searchTerms.length)
      .map(x => x.key);
  };

  const handleIconClick = (texture) => {
    setSelected(texture);
  };

  const onSearch = (event) => {
    setPage(1);
    setSearch(event.target.value as string);
  };

  const onPageChange = (event, page: number) => {
    setPage(page);
  };

  useEffect(() => {
    let selectedPage = Math.floor(textures.indexOf(currentTexture) / tagsPerPage) + 1;
    setSearch("");
    setSelected(currentTexture);
    setPage(selectedPage);
  }, [open]);

  const textures = getSearchedTextures();
  const iconChanged = (selected !== currentTexture);

  const tagsPerPage = tagIconsPerRow * tagRowsPerPage;
  const numPages = Math.ceil(textures.length / tagsPerPage);

  const texturesForPage = textures.slice(
    (page-1) * tagsPerPage,
    page * tagsPerPage
  );

  return (
    <DraggableDialog
      open={open}
      onClose={onClose}
      title={t("tags.edit-change-icon")}
    >
      <DialogContent dividers className={classes.content}>
        <Typography
          variant="caption"
          className={classes.tagIconFilename}
        >
          <b>{t("tags.edit-file-name")}: </b>{fileName}
        </Typography>

        <div className={classes.tagIconSearchParent}>
          <TextField
            label={t("tags.edit-search-icon")}
            type="search"
            onChange={onSearch}
            value={search}
            size="small"
          />
        </div>

        <div>
          {texturesForPage.map(texture => (
            <div key={texture} className={classes.tagIconParent}>
              <img
                alt=""
                title={texture}
                className={classes.tagIconImage}
                src={getTagTexturePath(texture)}
                onClick={() => {
                  handleIconClick(texture);
                }}
              />

              {(texture === selected) && (<CheckCircleIcon
                fontSize="small"
                className={classes.tagIconCheck}
              />)}
            </div>
          ))}
        </div>

        <div className={classes.tagIconPageParent}>
          <Pagination
            page={page}
            count={numPages}
            onChange={onPageChange}
            className={classes.tagIconPagination}
          ></Pagination>
        </div>

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t("buttons.cancel")}
        </Button>

        <Button
          onClick={() => {
            onSubmit(selected);
          }}
          color="primary"
          disabled={!iconChanged}
        >
          {t("buttons.apply-changes")}
        </Button>
      </DialogActions>

    </DraggableDialog>
  );
};

export const TagDetailsDialog = (props) => {
  const viewer = props.viewer as Viewer;

  const {t} = useTranslation();

  const [tagToEdit, setTagToEdit] = useState(null);

  const formatter = viewer.getMeasurementFormatter();
  const precision = formatter.getVectorPrecision(LocalScene.viewProjection);

  const onRowClick = (row, index) => {
    if (index === 3) {
      // Copy position to clipboard
      copyTextToClipboard(row.copyText);
      toast.success(t("toast.copied-to-clipboard"));
    } else {
      // Jump to nearest camera position
      viewer.jumpToTag(row.position, true);
    }
  };

  const onEditClick = (row) => {
    setTagToEdit(row.tagID);
  };

  const jumpToParameters = {
    pointer: true,
  };

  const clipboardParameters = {
    pointer: true,
    tooltip: t("tags.copy-to-clipboard")
  };

  const columns = [
    {
      id: 'tag',
      label: '#',
      numeric: true,
      ...jumpToParameters
    },
    {
      id: 'name',
      label: t("tags.table-name"),
      wordBreak: true,
      ...jumpToParameters
    },
    {
      id: 'comment',
      label: t("tags.table-comments"),
      wordBreak: true,
      ...jumpToParameters
    },
    {
      id: 'coordinate',
      label: t("tags.table-position"),
      ...clipboardParameters
    }
  ];

  const rows = props.tags.map((tag, index) => {
    const isAerialTag = !("z" in tag);
    const x = tag.x.toFixed(precision.x);
    const y = tag.y.toFixed(precision.y);
    const z = isAerialTag ? "N/A" : tag.z.toFixed(precision.z);

    const copyText = isAerialTag
      ? `(${x}, ${y})`
      : `(${x}, ${y}, ${z})`;

    const position = isAerialTag
      ? [tag.x, tag.y]
      : [tag.x, tag.y, tag.z];

    const coordinate = (
      <div style={{whiteSpace: "nowrap"}}>
        <b>X:</b> {x}<br/>
        <b>Y:</b> {y}<br/>
        {isAerialTag ? "" : <React.Fragment>
          <b>Z:</b> {z}<br/>
        </React.Fragment>}
      </div>
    );

    return {
      tagID: tag.id,
      tag: index + 1,
      name: tag.name,
      comment: tag.comment,
      id: index,
      position,
      copyText,
      coordinate
    };
  });

  return (
    <React.Fragment>
      {/* Details dialog */}
      <DraggableDialog
        open={props.open}
        title={props.title}
        onClose={props.onClose}
      >
        <DialogContent>
          <EnhancedTable
            dense
            editable={!isStaticSite}
            columns={columns}
            rows={rows}
            onEditClick={onEditClick}
            onRowClick={onRowClick}
          />
        </DialogContent>
      </DraggableDialog>

      {/* Edit Tag Dialog */}
      <TagEditDialog
        tagToEdit={tagToEdit}
        setTagToEdit={setTagToEdit}
      />
    </React.Fragment>
  );
};

export const TagDeleteDialog = (props) => {
  const {open, setOpen, toDelete} = props;

  const dispatch = useDispatch();
  const {t} = useTranslation();

  const onTagDeleteClose = () => {
    setOpen(false);
  };

  const onTagDeleteSubmit = () => {
    dispatch(deleteAssetData(toDelete));
    onTagDeleteClose();
    toast.success(t("toast.tag-removed", {
      count: numTagsToDelete
    }));
  };

  const numTagsToDelete = toDelete.length;

  return (
    <PromptDialog
      open={open}
      onCancel={onTagDeleteClose}
      onSubmit={onTagDeleteSubmit}
      title={t("dialog.delete-tags")}
      prompt={t("toast.tag-remove-prompt", {
        count: numTagsToDelete
      })}
      button={t("buttons.delete")}
    />
  );
};

export const TagEditDialog = (props) => {
  const {tagToEdit, setTagToEdit} = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const assets = useSelector(selectAllAssets);
  const tagAssets = getTagAssets(assets);

  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");

  const onTagEditClose = () => {
    setTagToEdit(null);
  };

  const onTagEditSubmit = () => {
    dispatch(changeTagContent({
      tagID: tagToEdit,
      name: title,
      comment: comment
    }));

    onTagEditClose();
    toast.success(t("toast.tag-details-update"));
  };

  useEffect(() => {
    if (editTagOpen) {
      tagAssets.forEach(asset => {
        const tag = asset.data.find(x => x.id === tagToEdit);
        if (!tag) return;

        setTitle(tag.name);
        setComment(tag.comment);
      });
    } else {
      setTitle("");
      setComment("");
    }
  }, [tagToEdit]);

  const editTagOpen = !!tagToEdit;
  const titleMissing = title.trim() === "";
  const canSubmitEdit = !titleMissing;

  return (
    <Dialog
      open={editTagOpen}
      onClose={onTagEditClose}
      fullWidth
      maxWidth={"xs"}
    >
      <DialogTitle>
        {t("dialog.edit-tag")}
      </DialogTitle>

      <DialogContent>
        <InputLabel className={clsx("", {
          [classes.asterisk]: titleMissing
        })}>
          {t("tags.tag-title")}
        </InputLabel>

        <TextField
          fullWidth
          value={title}
          className={classes.textField}
          onChange={(event) => {
            setTitle(event.target.value as string);
          }}
        />

        <InputLabel>
          {t("tags.tag-comments")}
        </InputLabel>

        <TextField
          fullWidth
          multiline
          value={comment}
          className={classes.textField}
          onChange={(event) => {
            setComment(event.target.value as string);
          }}
        />

      </DialogContent>

      <DialogActions>
        <Button
          onClick={onTagEditClose}
          color="primary"
        >
          {t("buttons.cancel")}
        </Button>

        <Button
          onClick={onTagEditSubmit}
          color="primary"
          disabled={!canSubmitEdit}
        >
          {t("buttons.save-changes")}
        </Button>
      </DialogActions>

    </Dialog>
  );
};

export const TagCreateDialog = (props) => {
  const {open, setOpen, coordinate} = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const assets = useSelector(selectAllAssets);
  const tagAssets = getTagAssets(assets);

  // Conditional selector needs shallowEqual
  const folders = useSelector(selectAllFolders)
    .filter(x => !x.isDefault);

  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [collectionID, setCollectionID] = useState("");
  const [collectionName, setCollectionName] = useState("");
  const [folderID, setFolderID] = useState("__empty");
  const [createNew, setCreateNew] = useState(false);
  const [iconUpdateOpen, setIconUpdateOpen] = useState(false);
  const [assetTexture, setAssetTexture] = useState(defaultTagTexture);

  const onTagAddClose = () => {
    setOpen(false);
  };

  const onTagAddSubmit = () => {
    onTagAddClose();

    const newTagData = {
      name: title,
      comment: comment,
      x: coordinate[0],
      y: coordinate[1],
    } as TagData;

    if (coordinate.length === 3) {
      newTagData.z = coordinate[2];
    }

    if (createNew) {
      let assetFolderID = (folderID === "__empty")
        ? getNewDefaultFolder(dispatch)
        : folderID;

      // Create new CSV with a single tag
      const action =  dispatch(addAsset(assetFolderID,
        `${collectionName}.csv`, AssetType.Tag, [newTagData]));

      // Update texture
      dispatch(changeTexture({
        assetID: action.payload.id,
        texture: assetTexture
      }));
    } else {
      // Add new tag to collection
      dispatch(addNewTag({
        assetID: collectionID,
        tagData: newTagData
      }));
    }

    toast.success(t("toast.tag-added"));
  };

  const resetDefaultValues = () => {
    setTitle("");
    setComment("");
    setFolderID("__empty");
    setCollectionName("");
    setCreateNew(!hasExistingCSVs);
    setAssetTexture(defaultTagTexture);
    setIconUpdateOpen(false);

    if (hasExistingCSVs) {
      setCollectionID(tagCollectionIDs[0]);
    }
  };

  useEffect(() => {
    resetDefaultValues();
  }, [open]);

  const hasExistingCSVs = tagAssets.length > 0;
  const tagCollectionIDs = tagAssets.map(x => x.id);

  /* Determine valid state for new tag */
  let canSubmitNew = true;
  const titleMissing = title.trim() === "";
  const nameMissing = collectionName.trim() === "";
  canSubmitNew = canSubmitNew && !titleMissing;
  canSubmitNew = canSubmitNew && !(nameMissing && createNew);

  return (
    <React.Fragment>
      {/* New tag dialog */}
      <Dialog
        open={open}
        onClose={onTagAddClose}
        fullWidth
        maxWidth={"xs"}
      >
        <DialogTitle>
          {t("tags.new-title")}
        </DialogTitle>

        <DialogContent>

          {hasExistingCSVs && (<div className={clsx(classes.checkBoxWithLabel, classes.textField)}>
            {/* Create new CSV */}
            <InputLabel>
              {t("tags.new-csv")}
            </InputLabel>

            <Checkbox
              checked={createNew}
              onChange={(event) => {
                setCreateNew(event.target.checked);
              }}
              size="small"
              color="secondary"
            />
          </div>)}

          {!createNew && (<React.Fragment>
            {/* Existing csv collections */}
            <InputLabel>
              {t("tags.existing-csv")}
            </InputLabel>
            <Select
              fullWidth
              value={collectionID}
              className={classes.textField}
              onChange={(event) => {
                setCollectionID(event.target.value as string);
              }}
            >
              {tagAssets.map(tagCSV =>
                (<MenuItem key={tagCSV.id} value={tagCSV.id}>{tagCSV.name}</MenuItem>)
              )}
            </Select>
          </React.Fragment>)}


          {createNew && (<React.Fragment>
            {/* Default Icon */}
            <div className={clsx(classes.avatarParent, classes.textField)}>
              <InputLabel>
                {t("tags.default-icon")}
              </InputLabel>
              <Avatar
                src={getTagTexturePath(assetTexture)}
                onClick={() => {
                  setIconUpdateOpen(true);
                }}
                className={classes.tagAvatar}
              />
            </div>

            {/* New folder */}
            <InputLabel>
              {t("tags.csv-folder")}
            </InputLabel>
            <Select
              fullWidth
              value={folderID}
              className={classes.textField}
              onChange={(event) => {
                setFolderID(event.target.value as string);
              }}
            >
              <MenuItem value={"__empty"}>
                {t("tags.new-folder")}
              </MenuItem>
              <hr className={classes.selectDivider}/>
              {folders.map(folder => {
                const localizedFolderName = t(folder.name);

                return (
                  <MenuItem key={folder.id} value={folder.id}>
                    {localizedFolderName}
                  </MenuItem>
                );
              })}
            </Select>

            {/* New CSV name */}
            <InputLabel className={clsx("", {
              [classes.asterisk]: nameMissing
            })}>
              {t("tags.new-csv-name")}
            </InputLabel>
            <TextField
              fullWidth
              value={collectionName}
              className={classes.textField}
              onChange={(event) => {
                setCollectionName(event.target.value as string);
              }}
            />
          </React.Fragment>)}

          <InputLabel className={clsx("", {
            [classes.asterisk]: titleMissing
          })}>
            {t("tags.new-tag-title")}
          </InputLabel>
          <TextField
            fullWidth
            value={title}
            className={classes.textField}
            onChange={(event) => {
              setTitle(event.target.value as string);
            }}
          />

          <InputLabel>
            {t("tags.new-tag-comments")}
          </InputLabel>
          <TextField
            fullWidth
            multiline
            value={comment}
            className={classes.textField}
            onChange={(event) => {
              setComment(event.target.value as string);
            }}
          />

        </DialogContent>

        <DialogActions>
          <Button
            onClick={onTagAddClose}
            color="primary"
          >
            {t("buttons.cancel")}
          </Button>

          <Button
            onClick={onTagAddSubmit}
            color="primary"
            disabled={!canSubmitNew}
          >
            {t("buttons.submit")}
          </Button>
        </DialogActions>

      </Dialog>

      {/* Tag Icon */}
      <TagIconDialog
        open={iconUpdateOpen}
        fileName={t("tags.new-tag-name")}
        currentTexture={assetTexture}
        onSubmit={(selected) => {
          setAssetTexture(selected);
          setIconUpdateOpen(false);
        }}
        onClose={() => {
          setIconUpdateOpen(false);
        }}
      />
    </React.Fragment>
  );
};
